import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';

const STORAGE_KEY = 'offline_location_queue';
const MAX_QUEUE_SIZE = 10000; // Maximum points to store

export function useOfflineLocationQueue(registrationId) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueSize, setQueueSize] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const syncInProgressRef = useRef(false);

  // Load initial queue size
  useEffect(() => {
    const queue = getQueue();
    setQueueSize(queue.length);
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('[OfflineQueue] Connection restored');
      setIsOnline(true);
      // Attempt to sync when connection is restored
      syncQueue();
    };

    const handleOffline = () => {
      console.log('[OfflineQueue] Connection lost');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [registrationId]);

  // Get queue from localStorage
  const getQueue = useCallback(() => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('[OfflineQueue] Error reading queue:', error);
      return [];
    }
  }, []);

  // Save queue to localStorage
  const saveQueue = useCallback((queue) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
      setQueueSize(queue.length);
    } catch (error) {
      console.error('[OfflineQueue] Error saving queue:', error);
      // If storage is full, remove oldest entries
      if (error.name === 'QuotaExceededError') {
        const trimmedQueue = queue.slice(-Math.floor(MAX_QUEUE_SIZE / 2));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedQueue));
        setQueueSize(trimmedQueue.length);
      }
    }
  }, []);

  // Add location to queue (used when offline)
  const addToQueue = useCallback((locationData) => {
    const queue = getQueue();
    
    const queueItem = {
      registration_id: registrationId,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      altitude: locationData.altitude || null,
      accuracy: locationData.accuracy || null,
      speed: locationData.speed || null,
      heading: locationData.heading || null,
      timestamp: locationData.timestamp || new Date().toISOString(),
      queued_at: new Date().toISOString()
    };

    // Add to queue, respecting max size
    if (queue.length >= MAX_QUEUE_SIZE) {
      queue.shift(); // Remove oldest
    }
    queue.push(queueItem);
    
    saveQueue(queue);
    console.log(`[OfflineQueue] Added location. Queue size: ${queue.length}`);
    
    return queue.length;
  }, [registrationId, getQueue, saveQueue]);

  // Sync queue with server
  const syncQueue = useCallback(async () => {
    if (syncInProgressRef.current) {
      console.log('[OfflineQueue] Sync already in progress');
      return;
    }

    const queue = getQueue();
    if (queue.length === 0) {
      console.log('[OfflineQueue] Queue is empty, nothing to sync');
      return;
    }

    if (!navigator.onLine) {
      console.log('[OfflineQueue] Still offline, cannot sync');
      return;
    }

    syncInProgressRef.current = true;
    setIsSyncing(true);
    setSyncError(null);

    try {
      const token = localStorage.getItem('token');
      
      // Filter queue items for current registration
      const itemsToSync = queue.filter(item => item.registration_id === registrationId);
      
      if (itemsToSync.length === 0) {
        console.log('[OfflineQueue] No items for current registration');
        syncInProgressRef.current = false;
        setIsSyncing(false);
        return;
      }

      console.log(`[OfflineQueue] Syncing ${itemsToSync.length} locations...`);

      // Send batch to server
      const response = await axios.post(
        `${API}/live-location/batch`,
        { locations: itemsToSync },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('[OfflineQueue] Sync successful:', response.data);

      // Remove synced items from queue
      const remainingQueue = queue.filter(item => item.registration_id !== registrationId);
      saveQueue(remainingQueue);
      
      setLastSyncTime(new Date().toLocaleTimeString());
      setSyncError(null);

    } catch (error) {
      console.error('[OfflineQueue] Sync failed:', error);
      setSyncError(error.response?.data?.detail || error.message);
    } finally {
      syncInProgressRef.current = false;
      setIsSyncing(false);
    }
  }, [registrationId, getQueue, saveQueue]);

  // Clear queue for current registration
  const clearQueue = useCallback(() => {
    const queue = getQueue();
    const remainingQueue = queue.filter(item => item.registration_id !== registrationId);
    saveQueue(remainingQueue);
    console.log('[OfflineQueue] Queue cleared for registration:', registrationId);
  }, [registrationId, getQueue, saveQueue]);

  // Get queue items for current registration
  const getQueueForRegistration = useCallback(() => {
    const queue = getQueue();
    return queue.filter(item => item.registration_id === registrationId);
  }, [registrationId, getQueue]);

  return {
    isOnline,
    queueSize,
    isSyncing,
    lastSyncTime,
    syncError,
    addToQueue,
    syncQueue,
    clearQueue,
    getQueueForRegistration,
    pendingCount: getQueueForRegistration().length
  };
}
